import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';

import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '@sh/services/auth/auth.service';
import { ToastService } from '@sh/services/toast/toast.service';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, RouterModule],
})
export class SignupComponent {
  public isSubmitting = false;
  public signupForm = new FormGroup({
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
    }),
    password: new FormControl('', {
      validators: [Validators.required, Validators.minLength(6)],
    }),
    confirmPassword: new FormControl('', {
      validators: [Validators.required],
    }),
  });

  constructor(
    private _authService: AuthService,
    private _router: Router,
    private _toastService: ToastService,
  ) {}

  signUpWithEmail() {
    if (this.isSubmitting) {
      return;
    }

    if (!this.signupForm.valid) {
      this.signupForm.markAllAsTouched();
      this._toastService.showToast(
        { message: 'Please fill in all fields correctly' },
        'error',
      );
      return;
    }

    const email = this.signupForm.value.email?.trim().toLowerCase();
    const password = this.signupForm.value.password;
    const confirmPassword = this.signupForm.value.confirmPassword;

    if (!email || !password || !confirmPassword) {
      return;
    }

    if (password !== confirmPassword) {
      this._toastService.showToast(
        { message: 'Passwords do not match' },
        'error',
      );
      return;
    }

    this.isSubmitting = true;
    this.signupForm.disable();

    return this._authService
      .signUpWithEmail(email, password)
      .then(() => {
        this._toastService.showToast(
          { message: 'Account created successfully!' },
          'success',
        );
        const redirectUrl = this._authService.redirectUrl;
        if (redirectUrl) {
          this._router.navigateByUrl(redirectUrl);
          this._authService.redirectUrl = null;
        } else {
          this._router.navigate(['/profile']);
        }
      })
      .catch((error) => {
        let message = 'Failed to create account';
        if (error.code === 'auth/email-already-in-use') {
          message = 'This email is already registered';
        } else if (error.code === 'auth/weak-password') {
          message = 'Password is too weak';
        } else if (error.code === 'auth/invalid-email') {
          message = 'Invalid email address';
        } else if (error.code === 'auth/network-request-failed') {
          message = 'Network error. Check your connection and try again';
        } else if (error.code === 'auth/operation-not-allowed') {
          message = 'Email sign up is not enabled right now';
        }
        this._toastService.showToast({ message }, 'error');
      })
      .finally(() => {
        this.isSubmitting = false;
        this.signupForm.enable();
      });
  }

  signUpWithGoogle() {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    return this._authService
      .signInWithGoogle()
      .then(() => {
        this._toastService.showToast(
          { message: 'Signed up successfully!' },
          'success',
        );
        const redirectUrl = this._authService.redirectUrl;
        if (redirectUrl) {
          this._router.navigateByUrl(redirectUrl);
          this._authService.redirectUrl = null;
        } else {
          this._router.navigate(['/profile']);
        }
      })
      .catch((error) => {
        let message = 'Failed to sign up with Google';
        if (error.code === 'auth/popup-closed-by-user') {
          message = 'Google sign up was cancelled';
        } else if (error.code === 'auth/popup-blocked') {
          message = 'Popup blocked. Allow popups and try again';
        } else if (error.code === 'auth/network-request-failed') {
          message = 'Network error. Check your connection and try again';
        }
        this._toastService.showToast({ message }, 'error');
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }
}
